import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { AIRCRAFT_DATABASE, getAircraftByCategory, getAircraftImage, getCollectibleImagePath } from "../data/aircraft.js";
import { processA380Purchase, getEOSStatus, RARE_AIRCRAFT } from "../systems/EOSSystem.js";
import { HUB_CAPACITY } from "../components/simple/ExclusiveHubOffer.jsx";
import { getReputationTier } from "../components/sim/GameLoopEngine.jsx";
import { getUnlockRequirements, getUnlockProgress, REP_THRESHOLDS, ROUTE_THRESHOLDS } from "../systems/FleetProgressionSystem.js";
import { api } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
// Icons removed - text-only UI

// ============================================================================
// MARKET CONFIGURATION
// ============================================================================

// REMOVED: TIER_AIRCRAFT_ACCESS - now using aircraft.tier from database

const CONDITION_MULTIPLIERS = {
  excellent: { price: 0.85, maintenance: 1.1, reliability: 0.99, label: "Excellent", color: "#22c55e" },
  good: { price: 0.70, maintenance: 1.3, reliability: 0.97, label: "Good", color: "#60ffd0" },
  fair: { price: 0.55, maintenance: 1.6, reliability: 0.94, label: "Fair", color: "#eab308" },
  needsWork: { price: 0.40, maintenance: 2.2, reliability: 0.88, label: "Needs Work", color: "#ef4444" },
};

const LEASE_TERMS = {
  12: { multiplier: 1.3, deposit: 2, label: "12 Months", penalty: 0.15 },
  36: { multiplier: 1.0, deposit: 1, label: "36 Months", penalty: 0.10 },
  60: { multiplier: 0.85, deposit: 0, label: "60 Months", penalty: 0.05 },
};

// Lease tier gates - purchases stay open for all, but leases require credit rating
const LEASE_TIER_REQUIREMENTS = {
  // Tier 1 - Regionals (both old and new keys for save compatibility)
  "E170": 1, "E175": 1, "E190": 1, "E195": 1,
  "Embraer-E170": 1, "Embraer-E175": 1, "Embraer-E190": 1, "Embraer-E195": 1,
  "CRJ700": 1, "CRJ900": 1,
  // Tier 2 - Narrowbodies (A220-300 is here, not tier 1)
  "A220-300": 2, "737-700": 2, "737-800": 2, "737-900ER": 2, "A319": 2, "A320": 2, "A320neo": 2, "A321": 2, "757-200": 2, "757-300": 2,
  // Tier 3 - Narrowbody+ / MAX
  "737 MAX 8": 3, "737 MAX 9": 3, "A321XLR": 3,
  // Tier 4 - Widebodies
  "767-300ER": 4, "787-8": 4, "787-9": 4, "787-10": 4, "A330-200": 4, "A330-300": 4, "A330-900neo": 4,
  // Tier 5 - Heavy Widebodies
  "777-200ER": 5, "777-300ER": 5, "A350-900": 5, "A350-1000": 5, "747-8": 5,
  // No Lease - Purchase Only (use both possible model names)
  "A380": null, "A380-800": null, // Cannot be leased
};

const LEASE_TIER_NAMES = {
  1: "Regional",
  2: "Narrowbody",
  3: "Narrowbody+",
  4: "Widebody",
  5: "Heavy Widebody",
};

// Legacy alias keys - these exist in AIRCRAFT_DATABASE for save compatibility
// but should NOT display in the market (would cause duplicates)
const LEGACY_ALIAS_KEYS = ["E170", "E175", "E190", "E195", "C919"];

// Helper to check if aircraft can be leased at player's tier
function canLeaseAircraft(model, playerTier) {
  const requiredTier = LEASE_TIER_REQUIREMENTS[model];
  if (requiredTier === null) return { canLease: false, reason: "purchase-only" };
  if (requiredTier === undefined) return { canLease: true }; // Not in list = no restriction
  if (playerTier >= requiredTier) return { canLease: true };
  return { canLease: false, reason: "tier", requiredTier, tierName: LEASE_TIER_NAMES[playerTier] || "Regional" };
}

const COLLECTOR_AIRCRAFT = [
  { id: "SPIRIT_STL", name: "Spirit of St. Louis", year: 1927, story: "Lindbergh's transatlantic pioneer", spawnRate: 0.0001, price: 2500000, repBonus: 300, capacity: 1, range: 4000 },
  { id: "ENOLA_GAY", name: "Enola Gay", year: 1945, story: "B-29 Superfortress - History's shadow", spawnRate: 0.000235, price: 8500000, repBonus: 400, capacity: 12, range: 3500 },
  { id: "CONCORDE", name: "Concorde", year: 1976, story: "Supersonic elegance, retired too soon", spawnRate: 0.005, price: 15000000, repBonus: 500, capacity: 100, range: 4500 },
  { id: "DC10_SHIP1", name: "DC-10 Ship 1", year: 1970, story: "First DC-10 ever built, restored", spawnRate: 0.005, price: 4200000, repBonus: 350, capacity: 250, range: 5500 },
  { id: "747_CITYEVERETT", name: "747 City of Everett", year: 1969, story: "The original Jumbo Jet", spawnRate: 0.003, price: 12000000, repBonus: 600, capacity: 366, range: 6000 },
  { id: "707_PIONEER", name: "Boeing 707-120", year: 1958, story: "Jet age pioneer", spawnRate: 0.004, price: 3500000, repBonus: 400, capacity: 140, range: 3000 },
  { id: "TRISTAR", name: "L-1011 TriStar", year: 1972, story: "Beautifully over-engineered", spawnRate: 0.006, price: 5500000, repBonus: 350, capacity: 250, range: 4500 },
  { id: "CARAVELLE", name: "Sud Aviation Caravelle", year: 1959, story: "French elegance", spawnRate: 0.004, price: 2800000, repBonus: 300, capacity: 80, range: 1500 },
  { id: "COMET4", name: "de Havilland Comet 4", year: 1958, story: "First commercial jet airliner", spawnRate: 0.003, price: 4000000, repBonus: 500, capacity: 74, range: 2500 },
  { id: "A300B1", name: "Airbus A300B1", year: 1974, story: "The first Airbus ever built", spawnRate: 0.004, price: 6000000, repBonus: 400, capacity: 250, range: 2500 },
  { id: "MD80_MADDOG", name: "MD-80 Mad Dog", year: 1980, story: "Last one flying - pencil jet legend", spawnRate: 0.008, price: 1800000, repBonus: 250, capacity: 155, range: 2500 },
  { id: "727_TRIJET", name: "Boeing 727-100", year: 1964, story: "Classic tri-jet workhorse", spawnRate: 0.007, price: 2200000, repBonus: 280, capacity: 131, range: 2400 },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatPrice(price) {
  if (price >= 1000000000) return `$${(price / 1000000000).toFixed(2)}B`;
  if (price >= 1000000) return `$${(price / 1000000).toFixed(1)}M`;
  if (price >= 1000) return `$${(price / 1000).toFixed(0)}K`;
  return `$${price}`;
}

// Rarity color scheme for rare aircraft reveal dialog
const RARITY_COLORS = {
  mythic:    { primary: "#a855f7", text: "#d8b4fe", label: "MYTHIC", button: "CLAIM" },
  legendary: { primary: "#f59e0b", text: "#fcd34d", label: "LEGENDARY", button: "CLAIM" },
  anomalous: { primary: "#ef4444", text: "#fca5a5", label: "ANOMALOUS", button: "...WHAT?" },
  relic:     { primary: "#b8860b", text: "#daa520", label: "RELIC", button: "CLAIM" },
  rare:      { primary: "#60ffd0", text: "#a7f3d0", label: "RARE", button: "CLAIM" },
};

function getRarityColor(rarity) {
  return RARITY_COLORS[rarity] || RARITY_COLORS.rare;
}

function generateUsedMarketStock(dayNumber, playerTier) {
  const seed = dayNumber * 12345;
  const random = (n) => {
    const x = Math.sin(seed + n) * 10000;
    return x - Math.floor(x);
  };

  const stock = [];
  
  // Get all aircraft available up to player's tier + 1 (can see one tier ahead)
  // A380 is a flagship - available new but never in used market
  // EOS aircraft (eos: true or price: null) are never in regular market - earned through rolls only
  // EOS COLLECTIBLES (rarity field) are ROLL EXCLUSIVE - never purchasable
  const availableModels = Object.entries(AIRCRAFT_DATABASE)
    .filter(([model, data]) => {
      if (LEGACY_ALIAS_KEYS.includes(model)) return false; // Hide legacy aliases
      if (data.category === "rare") return false;
      if (data.eos) return false;
      if (data.price === null) return false;
      if (!data.purchasePrice) return false;
      if (data.rarity && data.rarity !== "standard") return false; // EOS collectibles are roll exclusive
      if (model === "A380") return false;
      if ((data.tier || 1) > playerTier + 1) return false;
      return true;
    })
    .map(([model]) => model);

  // Generate 4-8 used aircraft
  const count = 4 + Math.floor(random(0) * 5);
  for (let i = 0; i < count; i++) {
    const modelIndex = Math.floor(random(i + 1) * availableModels.length);
    const model = availableModels[modelIndex];
    const aircraft = AIRCRAFT_DATABASE[model];
    if (!aircraft) continue;

    const conditions = Object.keys(CONDITION_MULTIPLIERS);
    const conditionIndex = Math.floor(random(i + 10) * conditions.length);
    const condition = conditions[conditionIndex];
    const conditionData = CONDITION_MULTIPLIERS[condition];

    const age = 2 + Math.floor(random(i + 20) * 20);
    const cycles = age * (800 + Math.floor(random(i + 30) * 400));
    const hours = cycles * 2.5;

    stock.push({
      id: `USED-${dayNumber}-${i}`,
      model,
      aircraft,
      condition,
      conditionData,
      age,
      cycles,
      hours,
      price: Math.round(aircraft.purchasePrice * conditionData.price),
      maintenanceMult: conditionData.maintenance,
      reliability: conditionData.reliability,
      isUsed: true,
      requiredTier: aircraft.tier,
      isLocked: false, // All aircraft available - price is the only barrier
    });
  }

  // Check for collector spawn
  COLLECTOR_AIRCRAFT.forEach((collector, idx) => {
    if (random(100 + idx) < collector.spawnRate) {
      stock.push({
        id: `COLLECTOR-${collector.id}`,
        model: collector.id,
        name: collector.name,
        year: collector.year,
        story: collector.story,
        price: collector.price,
        repBonus: collector.repBonus,
        capacity: collector.capacity,
        range: collector.range,
        isCollector: true,
        condition: "museum",
        conditionData: { label: "Museum Quality", color: "#f59e0b" },
        isLocked: false, // Collectors are never locked
      });
    }
  });

  // BUG-005 FIX: Sort by price ascending (cheapest first)
  return stock.sort((a, b) => (a.price || 0) - (b.price || 0));
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AircraftMarketPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { worldState, updateWorldState, addEvent, updateCash } = useWorldState();
  const [activeTab, setActiveTab] = useState("new");
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [showLeaseDialog, setShowLeaseDialog] = useState(false);
  const [showRareRollDialog, setShowRareRollDialog] = useState(false);
  const [leaseLoading, setLeaseLoading] = useState(false);
  const [rareRollResult, setRareRollResult] = useState(null);
  const [selectedLeaseTerm, setSelectedLeaseTerm] = useState(36);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityMessage, setCapacityMessage] = useState("");
  // Delivery is always immediate in Simple mode - no waiting period
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);

  // TIER: Always derive from RepScore using getReputationTier - single source of truth
  const tier = getReputationTier(worldState?.reputation?.currentScore || 0);
  const cash = worldState?.airline?.cash || 0;
  const hasEOS = worldState?.eos?.hasEOS || false; // EOS is at worldState.eos, not reputation.eos
  const dayNumber = worldState?.dayNumber || 1;
  const ownedAircraft = worldState?.aircraft || [];

  // Simple Mode delivery pricing
  const isSimpleMode = worldState?.gameMode === 'simple' || worldState?.gameMode === 'arcade';
  // Expedited vouchers removed - all purchases are now immediate

  // Get EOS status for display
  const eosStatus = getEOSStatus(worldState);

  // Generate used market stock
  const usedStock = useMemo(() => {
    return generateUsedMarketStock(dayNumber, tier);
  }, [dayNumber, tier]);

  // Get new aircraft - use tier property from database
  // A380 is available to anyone with $350M (no roll). Gold A380 in EOS tab triggers roll.
  // EOS aircraft (eos: true or price: null) are never in regular market - earned through rolls only
  // EOS COLLECTIBLES (rarity field) are ROLL EXCLUSIVE - never purchasable
  // SIMULATION MODE: Fleet unlocks restrict access to narrowbody/widebody/heavy
  const isSimulationMode = worldState?.gameMode === 'simulation';
  const fleetUnlocks = worldState?.fleetUnlocks || { regional: true, narrowbody: false, widebody: false, heavy: false };


  const newAircraft = useMemo(() => {
    const all = [];
    Object.entries(AIRCRAFT_DATABASE).forEach(([model, data]) => {
      if (LEGACY_ALIAS_KEYS.includes(model)) return; // Hide legacy aliases (E170, E175, etc.)
      if (data.category === "rare") return; // Rare are EOS only
      if (data.eos) return; // EOS aircraft are earned through rolls, not purchased
      if (data.price === null) return; // Null price means EOS only
      if (!data.purchasePrice) return; // No purchasePrice means not for sale
      if (data.rarity && data.rarity !== "standard") return; // EOS collectibles are roll exclusive

      // Use the tier property from the aircraft database
      const requiredTier = data.tier || 1;

      // Determine if locked based on fleet progression (Simulation mode only)
      let isLocked = false;
      let lockReason = null;

      if (isSimulationMode) {
        const category = (data.category || 'regional').toLowerCase();
        switch (category) {
          case 'regional':
            isLocked = false;
            break;
          case 'narrowbody':
            isLocked = !fleetUnlocks.narrowbody;
            if (isLocked) lockReason = 'narrowbody';
            break;
          case 'widebody':
            isLocked = !fleetUnlocks.widebody;
            if (isLocked) lockReason = 'widebody';
            break;
          case 'heavy':
          case 'superheavy':
            isLocked = !fleetUnlocks.heavy;
            if (isLocked) lockReason = 'heavy';
            break;
          case 'bizjet':
            // Business jets require narrowbody unlock
            isLocked = !fleetUnlocks.narrowbody;
            if (isLocked) lockReason = 'narrowbody';
            break;
          default:
            isLocked = false;
        }
      }

      all.push({
        ...data,
        id: model,  // DB key for image lookup
        displayName: data.model || model,  // Friendly name for display
        model,      // Keep DB key as model (after spread so it's not overwritten)
        requiredTier,
        isLocked,
        lockReason,
      });
    });
    // Sort: unlocked first, then by price ascending
    return all.sort((a, b) => {
      // Unlocked before locked
      if (a.isLocked !== b.isLocked) {
        return a.isLocked ? 1 : -1;
      }
      // Then by price
      return (a.purchasePrice || 0) - (b.purchasePrice || 0);
    });
  }, [tier, isSimulationMode, fleetUnlocks?.regional, fleetUnlocks?.narrowbody, fleetUnlocks?.widebody, fleetUnlocks?.heavy]);


  // Count owned by type
  const ownedCount = useMemo(() => {
    const counts = {};
    ownedAircraft.forEach(ac => {
      counts[ac.model] = (counts[ac.model] || 0) + 1;
    });
    return counts;
  }, [ownedAircraft]);

  // Filter aircraft based on search query
  const filteredUsedStock = useMemo(() => {
    if (!searchQuery) return usedStock;
    const query = searchQuery.toLowerCase();
    return usedStock.filter(item => {
      const model = (item.displayName || item.model || item.name || '').toLowerCase();
      const manufacturer = (item.aircraft?.manufacturer || '').toLowerCase();
      return model.includes(query) || manufacturer.includes(query);
    });
  }, [usedStock, searchQuery]);

  const filteredNewAircraft = useMemo(() => {
    if (!searchQuery) return newAircraft;
    const query = searchQuery.toLowerCase();
    return newAircraft.filter(item => {
      const model = (item.model || '').toLowerCase();
      const manufacturer = (item.manufacturer || '').toLowerCase();
      return model.includes(query) || manufacturer.includes(query);
    });
  }, [newAircraft, searchQuery]);

  
  // Generate a unique registration number
  const generateRegistration = () => {
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const letter1 = letters[Math.floor(Math.random() * letters.length)];
    const letter2 = letters[Math.floor(Math.random() * letters.length)];
    const num = Math.floor(Math.random() * 900) + 100;
    return `N${num}${letter1}${letter2}`;
  };

  // Handle purchase - uses local state (dev mode) with optional API sync
  // Simple mode: Immediate delivery, no waiting period, no extra fees
  const handlePurchase = async (aircraft, quantity = 1) => {
    // All aircraft available - price is the only barrier (Gold A380 still requires EOS)
    const basePrice = aircraft.price || aircraft.purchasePrice;
    const totalPrice = basePrice * quantity; // Total for all aircraft

    if (cash < totalPrice) {
      addEvent({ type: "PURCHASE_FAILED", severity: "error", message: `Insufficient funds for ${quantity}x ${aircraft.model || aircraft.name}` });
      return;
    }

    // Check hub capacity - ALL modes have 30 plane per hub limit
    const allHubs = worldState?.ownedHubs || worldState?.airline?.hubs || [worldState?.airline?.hub || "KPDX"];
    const totalCapacity = allHubs.length * HUB_CAPACITY;
    const currentAircraft = worldState?.aircraft || [];
    const totalAircraftCount = currentAircraft.length;

    if (totalAircraftCount + quantity > totalCapacity) {
        // Calculate aircraft count per hub for detailed message
        const hubCounts = {};
        allHubs.forEach(hub => hubCounts[hub] = 0);
        currentAircraft.forEach(aircraft => {
          const hub = aircraft.location || aircraft.homeBase || allHubs[0];
          if (hubCounts[hub] !== undefined) hubCounts[hub]++;
        });

        // Build detailed message showing which hubs are full
        const hubStatuses = allHubs.map(hub => `${hub}: ${hubCounts[hub]}/${HUB_CAPACITY}`);
        const fullHubs = allHubs.filter(hub => hubCounts[hub] >= HUB_CAPACITY);

        let message;
        if (allHubs.length === 1) {
          // Single hub at capacity
          message = `Hub at capacity (${totalAircraftCount}/${HUB_CAPACITY}). Purchase another hub to expand your fleet.`;
        } else if (fullHubs.length === allHubs.length) {
          // All hubs are full
          message = `All hubs at capacity (${hubStatuses.join(', ')}). Purchase another hub to expand your fleet.`;
        } else if (fullHubs.length > 0) {
          // Some hubs are full
          const remaining = totalCapacity - totalAircraftCount;
          message = `Some hubs at capacity. ${hubStatuses.join(', ')}. ${remaining > 0 ? `${remaining} slots available.` : ''} ${fullHubs.length === allHubs.length - 1 ? 'Purchase another hub to continue growing.' : ''}`;
        } else {
          // Generic message if somehow we hit this case
          const remaining = totalCapacity - totalAircraftCount;
          message = `Fleet at capacity (${totalAircraftCount}/${totalCapacity} across ${allHubs.length} hub${allHubs.length > 1 ? 's' : ''}). ${remaining > 0 ? `Can only add ${remaining} more.` : 'Expand to a new hub to continue growing.'}`;
        }

        // Show styled modal instead of alert
        setCapacityMessage(message);
        setShowCapacityModal(true);

        addEvent({
          type: "PURCHASE_FAILED",
          severity: "warning",
          message
        });
        return;
      }

    setIsPurchasing(true);
    setPurchaseError(null);

    try {
      // LOCAL STATE PURCHASE (works with cheat console)
      const aircraftModel = aircraft.model || aircraft.id;
      const aircraftData = AIRCRAFT_DATABASE[aircraftModel] || aircraft;

      // Get all owned hubs for capacity distribution
      const allHubs = worldState?.ownedHubs || worldState?.airline?.hubs || [worldState?.airline?.hub || "KPDX"];
      const currentAircraft = worldState?.aircraft || [];

      // Aircraft available immediately - no delivery wait
      const initialStatus = "available";

      // Create array of new aircraft - distribute across hubs if needed
      const newAircraftArray = [];
      // Track how many aircraft we're adding to each hub (to properly spread bulk purchases)
      const hubAircraftCounts = {};
      for (const hub of allHubs) {
        hubAircraftCounts[hub] = currentAircraft.filter(a => a.location === hub || a.homeBase === hub).length;
      }

      for (let i = 0; i < quantity; i++) {
        // Find a hub with space for this aircraft
        let assignHub = allHubs[0];
        for (const hub of allHubs) {
          if (hubAircraftCounts[hub] < HUB_CAPACITY) {
            assignHub = hub;
            hubAircraftCounts[hub]++; // Increment for next iteration
            break;
          }
        }

        newAircraftArray.push({
          id: `aircraft-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`,
          registration: generateRegistration(),
          ...aircraftData,
          model: aircraftModel,  // AFTER spread so DB key isn't overwritten by display name
          status: initialStatus,
          location: assignHub,
          homeBase: assignHub,
          totalFlightHours: 0,
          totalCycles: 0,
          degradation: 0,
          purchasePrice: basePrice,
          purchaseDate: new Date().toISOString(),
          rarity: "standard",
          isRareAircraft: false,
        });
      }

      // Check for EOS rare roll (ONLY if aircraft has triggersRareRoll flag - i.e. Gold A380)
      // Regular A380 does NOT trigger roll. Only Gold A380 from EOS store does.
      let rareRollTriggered = false;
      let rareAircraftWon = null;

      // EOS rare roll - only triggers for Gold A380 with EOS active (only once even if buying multiple)
      if (aircraft.triggersRareRoll && hasEOS) {
        try {
          const purchaseResult = processA380Purchase(worldState);

          if (purchaseResult.success && purchaseResult.rareAircraft) {
            rareRollTriggered = true;
            const actualRarity = purchaseResult.rollResult?.rarity || purchaseResult.rareAircraft.rarity;
            // Find a hub with space for rare aircraft
            let rareHub = allHubs[0];
            for (const hub of allHubs) {
              if (hubAircraftCounts[hub] < HUB_CAPACITY) {
                rareHub = hub;
                break;
              }
            }
            rareAircraftWon = {
              id: `rare-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              registration: `NX${Math.floor(Math.random() * 900) + 100}R`,
              model: purchaseResult.rareAircraft.model || purchaseResult.rareAircraft.id,
              name: purchaseResult.rareAircraft.name,
              ...purchaseResult.rareAircraft,
              rarity: actualRarity,
              isRareAircraft: true,
              status: "available",
              location: rareHub,
              homeBase: rareHub,
            };
          }
        } catch (err) {
          console.error("[EOS] Roll error:", err);
        }
      }

      // Update local state
      updateWorldState(prev => ({
        ...prev,
        aircraft: rareAircraftWon
          ? [...prev.aircraft, ...newAircraftArray, rareAircraftWon]
          : [...prev.aircraft, ...newAircraftArray],
        airline: {
          ...prev.airline,
          cash: prev.airline.cash - totalPrice,
        },
      }));

      // Show rare roll dialog if triggered
      if (rareRollTriggered && rareAircraftWon) {
        setRareRollResult({
          rarity: rareAircraftWon.rarity,
          aircraft: rareAircraftWon,
          rollValue: Math.random(),
        });
        setShowRareRollDialog(true);
      }

      // Event message varies based on quantity
      const registrations = newAircraftArray.map(a => a.registration).join(', ');
      addEvent({
        type: "AIRCRAFT_PURCHASED",
        severity: "success",
        message: quantity > 1
          ? `${quantity}x ${aircraftModel} purchased for ${formatPrice(totalPrice)}!`
          : `${aircraftModel} (${registrations}) purchased for ${formatPrice(totalPrice)}!`,
      });
    } catch (err) {
      console.error("[PURCHASE] Error during purchase:", err);
      addEvent({
        type: "PURCHASE_FAILED",
        severity: "error",
        message: `Purchase failed: ${err.message}`,
      });
    } finally {
      setIsPurchasing(false);
      setShowPurchaseDialog(false);
      setSelectedAircraft(null);
      setPurchaseQuantity(1); // Reset quantity
    }
  };

  // Handle lease - calls server API for authoritative aircraft creation
  const handleLease = async (aircraft) => {
    // Check lease tier requirements
    const leaseCheck = canLeaseAircraft(aircraft.model, tier);
    if (!leaseCheck.canLease) {
      if (leaseCheck.reason === "purchase-only") {
        addEvent({ type: "LEASE_FAILED", severity: "error", message: `${aircraft.model} is available for purchase only` });
      } else {
        addEvent({ type: "LEASE_FAILED", severity: "error", message: `Your credit rating only allows leasing of ${leaseCheck.tierName} aircraft` });
      }
      return;
    }

    setLeaseLoading(true);

    // Check hub capacity - ALL modes have 30 plane per hub limit
    const allHubs = worldState?.ownedHubs || worldState?.airline?.hubs || [worldState?.airline?.hub || "KPDX"];
    const currentAircraftCount = worldState?.aircraft?.length || 0;
    const totalCapacity = allHubs.length * HUB_CAPACITY;

    if (currentAircraftCount >= totalCapacity) {
      const leaseMessage = allHubs.length === 1
        ? `Hub at capacity (${currentAircraftCount}/${HUB_CAPACITY}). Purchase another hub to expand your fleet.`
        : `All hubs at capacity. Purchase another hub to expand your fleet.`;

      setCapacityMessage(leaseMessage);
      setShowCapacityModal(true);

      addEvent({
        type: "LEASE_FAILED",
        severity: "error",
        message: leaseMessage
      });
      setLeaseLoading(false);
      return;
    }

    // Additional tier-based fleet cap for Simulation mode
    if (worldState?.gameMode !== 'simple') {
      const maxFleetByTier = { 1: 5, 2: 10, 3: 20, 4: 35, 5: 50 };
      const currentFleet = worldState.aircraft?.length || 0;
      const maxAllowed = maxFleetByTier[tier] || 5;

      if (currentFleet >= maxAllowed) {
        alert(`Tier ${tier} airlines limited to ${maxAllowed} aircraft. Upgrade your AOC.`);
        setLeaseLoading(false);
        return;
      }
    }

    const term = LEASE_TERMS[selectedLeaseTerm];
    const monthlyPayment = Math.round((aircraft.leasePrice || 100000) * term.multiplier);
    const deposit = monthlyPayment * term.deposit;

    if (cash < deposit) {
      addEvent({ type: "LEASE_FAILED", severity: "error", message: `Insufficient funds for lease deposit` });
      setLeaseLoading(false);
        return;
    }

    try {
      // LOCAL STATE LEASE (works like purchase - no API dependency)
      const aircraftModel = aircraft.model || aircraft.id;
      const aircraftData = AIRCRAFT_DATABASE[aircraftModel] || aircraft;

      // Find a hub with available capacity
      const allHubs = worldState?.ownedHubs || worldState?.airline?.hubs || [worldState?.airline?.hub || "KPDX"];
      const currentAircraft = worldState?.aircraft || [];
      let targetHub = allHubs[0];
      for (const hub of allHubs) {
        const aircraftAtHub = currentAircraft.filter(a => a.location === hub || a.homeBase === hub).length;
        if (aircraftAtHub < HUB_CAPACITY) {
          targetHub = hub;
          break;
        }
      }

      const newAircraft = {
        id: `lease-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        registration: generateRegistration(),
        ...aircraftData,
        model: aircraftModel,  // AFTER spread so DB key isn't overwritten by display name
        status: "available",
        location: targetHub,
        homeBase: targetHub,
        totalFlightHours: 0,
        totalCycles: 0,
        degradation: 0,
        isLeased: true,
        leaseTermMonths: selectedLeaseTerm,
        monthlyPayment,
        leaseStartDate: new Date().toISOString(),
      };

      updateWorldState(prev => ({
        ...prev,
        aircraft: [...prev.aircraft, newAircraft],
        airline: {
          ...prev.airline,
          cash: prev.airline.cash - deposit,
        },
      }));

      addEvent({
        type: "AIRCRAFT_LEASED",
        severity: "success",
        message: `${aircraftData.model || aircraftModel} leased! ${formatPrice(monthlyPayment)}/mo for ${selectedLeaseTerm} months.`,
      });

      setShowLeaseDialog(false);
      setLeaseLoading(false);
      setSelectedAircraft(null);

    } catch (error) {
      console.error('Lease failed:', error);
      setLeaseLoading(false);
      addEvent({
        type: "LEASE_FAILED",
        severity: "error",
        message: `Lease failed: ${error.message}`,
      });
    }
  };

  // Handle sell
  const handleSell = (aircraft) => {
    const baseValue = AIRCRAFT_DATABASE[aircraft.model]?.purchasePrice || 10000000;
    const conditionMult = aircraft.condition ? (CONDITION_MULTIPLIERS[aircraft.condition]?.price || 0.5) : 0.7;
    const sellPrice = Math.round(baseValue * conditionMult * 0.55); // Pawn shop margin

    updateWorldState(prev => ({
      ...prev,
      aircraft: prev.aircraft.filter(a => a.id !== aircraft.id),
      airline: {
        ...prev.airline,
        cash: prev.airline.cash + sellPrice,
      },
    }));

    addEvent({ 
      type: "AIRCRAFT_SOLD", 
      severity: "info", 
      message: `${aircraft.registration} sold for ${formatPrice(sellPrice)}` 
    });
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: "#0a0a0a", color: "#e5e5e5" }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/opscenter")}
              style={{ color: "#737373" }}
            >
              &lt; Ops Center
            </Button>
            <div>
              <h1 className="text-3xl font-bold" style={{ color: "#60ffd0" }}>{t('market.title')}</h1>
              <p className="text-sm" style={{ color: "#737373" }}>{t('market.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="text-2xl font-bold" style={{ color: "#22c55e" }}>{formatPrice(cash)}</div>
              <div className="text-xs" style={{ color: "#737373" }}>{t('market.availableCash')}</div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold" style={{ color: "#60ffd0" }}>{t('market.tier')} {tier}</div>
              <div className="text-xs" style={{ color: "#737373" }}>{hasEOS ? t('market.eosActive') : t('market.noEos')}</div>
            </div>
            <Button onClick={() => navigate("/WorldMap")} style={{ backgroundColor: "#3b82f6", color: "#fff" }}>
              {t('market.worldMap')}
            </Button>
            <Button onClick={() => navigate("/opscenter")} style={{ backgroundColor: "#60ffd0", color: "#000" }}>
              {t('market.opsCenter')}
            </Button>
            <Button onClick={() => navigate("/fleet")} style={{ backgroundColor: "#333", color: "#e5e5e5" }}>
              {t('market.yourFleet')} ({ownedAircraft.length})
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab("used")}
            style={activeTab === "used"
              ? { backgroundColor: "#60ffd0", color: "#000" }
              : { backgroundColor: "#1a1a1a", color: "#737373" }}
          >
            {t('market.usedMarket')}
          </Button>
          <Button
            onClick={() => setActiveTab("new")}
            style={activeTab === "new"
              ? { backgroundColor: "#60ffd0", color: "#000" }
              : { backgroundColor: "#1a1a1a", color: "#737373" }}
          >
            {t('market.newAircraft')}
          </Button>
          <Button
            onClick={() => setActiveTab("eos")}
            style={activeTab === "eos"
              ? { backgroundColor: "#f59e0b", color: "#000" }
              : { backgroundColor: "#1a1a1a", color: "#f59e0b" }}
          >
            {t('market.eosMarket')}
          </Button>
          <Button
            onClick={() => setActiveTab("fleet")}
            style={activeTab === "fleet"
              ? { backgroundColor: "#60ffd0", color: "#000" }
              : { backgroundColor: "#1a1a1a", color: "#737373" }}
          >
            {t('market.yourFleet')}
          </Button>
        </div>

        {/* Search Bar */}
        {(activeTab === "new" || activeTab === "used") && (
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search by model (737, A320) or manufacturer (Boeing, Airbus)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 rounded-lg text-lg"
              style={{
                backgroundColor: "#1a1a1a",
                borderColor: "#333",
                color: "#e5e5e5",
                borderWidth: "1px",
                borderStyle: "solid"
              }}
            />
          </div>
        )}

        {/* Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* USED MARKET */}
          {activeTab === "used" && filteredUsedStock.length === 0 && searchQuery && (
            <div className="col-span-full text-center py-12" style={{ color: "#525252" }}>
              <p className="text-lg mb-2">No aircraft found matching "{searchQuery}"</p>
              <p className="text-sm">Try searching for different models or manufacturers</p>
            </div>
          )}
          {activeTab === "used" && filteredUsedStock.map((item) => (
            <Card 
  key={item.model} 
  className="overflow-hidden cursor-pointer" 
  onClick={() => item.isLocked && alert(`${item.model} requires Tier ${item.requiredTier}\n\nComplete more flights to unlock.`)}
  style={{ 
                backgroundColor: "#0c0c0c", 
                borderColor: item.isCollector ? "#f59e0b" : item.isLocked ? "#333" : "#1a1a1a",
                opacity: item.isLocked ? 0.6 : 1,
              }}
            >
              {item.isCollector && (
                <div className="px-3 py-1 flex items-center gap-2" style={{ backgroundColor: "#3d2a0a" }}>
                  <span className="text-xs font-bold" style={{ color: "#f59e0b" }}>* RARE COLLECTOR FIND *</span>
                </div>
              )}
              <CardContent className="p-4">
                {/* Aircraft Image */}
                <div className="h-32 rounded mb-3 flex items-center justify-center overflow-hidden relative" style={{ backgroundColor: "#111" }}>
                  <img
                    src={getAircraftImage(item.model)}
                    alt={item.model}
                    className="w-full h-full object-contain object-center"
                    style={{ filter: "drop-shadow(2px 4px 6px rgba(0,0,0,0.5))" }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <span className="text-2xl hidden" style={{ color: "#333" }}>---</span>
                  {item.isLocked && (
  <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded" style={{ backgroundColor: "rgba(0,0,0,0.85)", border: "1px solid #f59e0b" }}>
    <span className="text-xs font-bold" style={{ color: "#f59e0b" }}>Tier {item.requiredTier}</span>
  </div>
)}
                </div>
                
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold" style={{ color: "#e5e5e5" }}>{item.displayName || item.model}</h3>
                    {item.isCollector ? (
                      <p className="text-xs" style={{ color: "#737373" }}>{item.year} | {item.story}</p>
                    ) : (
                      <p className="text-xs" style={{ color: "#737373" }}>{item.aircraft?.manufacturer} | {item.age} years old</p>
                    )}
                  </div>
                  <Badge style={{ backgroundColor: item.conditionData.color, color: "#000" }}>
                    {item.conditionData.label}
                  </Badge>
                </div>

                {!item.isCollector && (
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3" style={{ color: "#a3a3a3" }}>
                    <div>{item.aircraft?.type === 'cargo' ? `${((item.aircraft?.cargoCapacity || 0) / 1000).toFixed(0)}k lbs` : `${item.aircraft?.capacity || 0} pax`}</div>
                    <div>{item.aircraft?.range} nm</div>
                    <div>{item.aircraft?.cruiseSpeed || 450} kts</div>
                    <div>{item.hours?.toLocaleString()} hrs</div>
                    <div>{item.maintenanceMult}x maint</div>
                  </div>
                )}

                {item.isCollector && (
                  <>
                    <div className="grid grid-cols-3 gap-2 text-xs mb-3" style={{ color: "#a3a3a3" }}>
                      <div>{item.capacity} pax</div>
                      <div>{item.range} nm</div>
                      <div>{item.cruiseSpeed || 300} kts</div>
                    </div>
                    <div className="mb-3 p-2 rounded" style={{ backgroundColor: "#1a1a0a" }}>
                      <div className="flex items-center gap-1 text-xs" style={{ color: "#eab308" }}>
                        <span>+{item.repBonus} Reputation</span>
                      </div>
                      <div className="text-xs mt-1" style={{ color: "#737373" }}>
                        Warning: Museum piece - terrible economics
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-xl font-bold" style={{ color: "#22c55e" }}>{formatPrice(item.price)}</div>
                  <Button
                    size="sm"
                    onClick={() => { setSelectedAircraft(item); setPurchaseError(null); setShowPurchaseDialog(true); }}
                    disabled={item.isLocked || cash < item.price}
                    style={!item.isLocked && cash >= item.price 
                      ? { backgroundColor: "#60ffd0", color: "#000" }
                      : { backgroundColor: "#333", color: "#666" }}
                  >
                    {item.isLocked ? `Tier ${item.requiredTier}` : cash >= item.price ? "Buy Now" : "Can't Afford"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* NEW MARKET */}
          {activeTab === "new" && filteredNewAircraft.length === 0 && searchQuery && (
            <div className="col-span-full text-center py-12" style={{ color: "#525252" }}>
              <p className="text-lg mb-2">No aircraft found matching "{searchQuery}"</p>
              <p className="text-sm">Try searching for different models or manufacturers</p>
            </div>
          )}
          {activeTab === "new" && filteredNewAircraft.map((item) => {
            // Get unlock progress for locked items
            const unlockProgress = item.isLocked && item.lockReason
              ? getUnlockProgress(item.lockReason, worldState)
              : null;
            const unlockReqs = item.isLocked && item.lockReason
              ? getUnlockRequirements(item.lockReason)
              : null;

            return (
            <Card
              key={item.model}
              className="overflow-hidden"
              style={{
                backgroundColor: item.isLocked ? "#080808" : "#0c0c0c",
                borderColor: item.isLocked ? "#333" : "#1a1a1a",
                opacity: item.isLocked ? 0.85 : 1,
              }}
            >
              <CardContent className="p-4">
                {/* Aircraft Image */}
                <div className="h-32 rounded mb-3 flex items-center justify-center relative overflow-hidden" style={{ backgroundColor: "#111" }}>
                  <img
                    src={getAircraftImage(item.model)}
                    alt={item.model}
                    className="w-full h-full object-contain object-center"
                    style={{
                      filter: item.isLocked
                        ? "grayscale(70%) brightness(0.6) drop-shadow(2px 4px 6px rgba(0,0,0,0.5))"
                        : "drop-shadow(2px 4px 6px rgba(0,0,0,0.5))"
                    }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <span className="text-2xl hidden" style={{ color: item.isLocked ? "#222" : "#333" }}>---</span>
                  {item.isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                      <span className="text-2xl font-bold" style={{ color: "#525252" }}>LOCKED</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold" style={{ color: "#e5e5e5" }}>{item.model}</h3>
                    <p className="text-xs" style={{ color: "#737373" }}>{item.manufacturer} | {item.category}</p>
                  </div>
                  {ownedCount[item.model] > 0 && (
                    <Badge style={{ backgroundColor: "#3b82f6", color: "#fff" }}>
                      Own {ownedCount[item.model]}
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mb-3" style={{ color: "#a3a3a3" }}>
                  <div>{item.type === 'cargo' ? `${((item.cargoCapacity || 0) / 1000).toFixed(0)}k lbs` : `${item.capacity || 0} pax`}</div>
                  <div>{item.range} nm</div>
                  <div>{item.cruiseSpeed || 450} kts</div>
                  <div>{item.fuelBurn} kg/hr fuel</div>
                  <div>{formatPrice(item.operatingCost)}/hr</div>
                </div>

                {/* Show unlock requirements for locked aircraft */}
                {item.isLocked && unlockReqs ? (
                  <div className="p-3 rounded mb-2" style={{ backgroundColor: "#111", border: "1px solid #333" }}>
                    <div className="text-xs font-bold mb-2" style={{ color: "#f59e0b" }}>
                      {unlockReqs.tierName.toUpperCase()} AIRCRAFT REQUIRE:
                    </div>
                    <ul className="text-xs space-y-1" style={{ color: "#737373" }}>
                      {unlockReqs.requirements.map((req, idx) => (
                        <li key={idx}>- {req.description}</li>
                      ))}
                    </ul>
                    {unlockProgress?.reputation && (
                      <div className="mt-2 pt-2" style={{ borderTop: "1px solid #222" }}>
                        <div className="text-xs" style={{ color: "#525252" }}>
                          Your rep: {unlockProgress.reputation.current.toLocaleString()} / {unlockProgress.reputation.target.toLocaleString()}
                        </div>
                        <div className="w-full h-1 mt-1 rounded overflow-hidden" style={{ backgroundColor: "#222" }}>
                          <div
                            className="h-full"
                            style={{
                              width: `${Math.min(100, (unlockProgress.reputation.current / unlockProgress.reputation.target) * 100)}%`,
                              backgroundColor: "#f59e0b"
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="text-xl font-bold" style={{ color: "#22c55e" }}>{formatPrice(item.purchasePrice)}</div>
                        <div className="text-xs" style={{ color: "#737373" }}>or {formatPrice(item.leasePrice)}/mo</div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => { setSelectedAircraft(item); setPurchaseError(null); setShowPurchaseDialog(true); }}
                        disabled={cash < item.purchasePrice}
                        style={cash >= item.purchasePrice
                          ? { backgroundColor: "#60ffd0", color: "#000" }
                          : { backgroundColor: "#333", color: "#666" }}
                      >
                        Buy
                      </Button>
                      {/* Leasing only available in Simulation mode */}
                      {worldState?.gameMode !== 'simple' && (() => {
                        const leaseCheck = canLeaseAircraft(item.model, tier);
                        const leaseDisabled = !leaseCheck.canLease || item.isLocked;
                        const leaseTooltip = leaseCheck.reason === "purchase-only"
                          ? "This aircraft is available for purchase only"
                          : leaseCheck.reason === "tier"
                            ? `Your credit rating only allows leasing of ${leaseCheck.tierName} aircraft at this time`
                            : null;
                        return (
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => { setSelectedAircraft(item); setShowLeaseDialog(true); }}
                            disabled={leaseDisabled}
                            title={leaseTooltip}
                            style={!leaseDisabled
                              ? { backgroundColor: "#3b82f6", color: "#fff" }
                              : { backgroundColor: "#333", color: "#666", cursor: "not-allowed" }}
                          >
                            Lease
                          </Button>
                        );
                      })()}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
          })}

          {/* EOS MARKET - GOLD A380 (viewable by all, purchasable by EOS holders) */}
          {activeTab === "eos" && (
            <>
              {/* Gold A380 - Same plane as regular A380, but triggers rare aircraft roll */}
              <Card className="overflow-hidden col-span-full lg:col-span-2" style={{ backgroundColor: "#0c0c0c", borderColor: "#f59e0b", borderWidth: "2px" }}>
                <div className="px-4 py-2" style={{ background: "linear-gradient(90deg, #3d2a0a, #5a3d0a)" }}>
                  <div className="flex items-center gap-2">
                    <span className="font-bold" style={{ color: "#f59e0b" }}>*** ELITE OPERATOR EXCLUSIVE - TRIGGERS RARE ROLL ***</span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    <div className="w-64 h-40 rounded flex items-center justify-center overflow-hidden" style={{ backgroundColor: "#1a1505", border: "2px solid #f59e0b" }}>
                      {AIRCRAFT_DATABASE["A380"]?.image ? (
                        <img
                          src={AIRCRAFT_DATABASE["A380"].image}
                          alt="Gold A380"
                          className="w-full h-full object-contain object-center"
                          style={{ filter: "drop-shadow(0 0 12px rgba(245,158,11,0.6)) sepia(0.3) saturate(1.5)" }}
                        />
                      ) : (
                        <span className="text-4xl" style={{ color: "#f59e0b" }}>A380</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-2" style={{ color: "#f59e0b" }}>GOLD A380</h3>
                      <p className="text-sm mb-4" style={{ color: "#a3a3a3" }}>
                        Same aircraft as the regular A380. But when purchased here, you trigger a <span style={{ color: "#f59e0b", fontWeight: "bold" }}>SECRET RARE AIRCRAFT ROLL</span>.
                        Roll can award: Rare, Legendary, Mythic, Relic, or Anomalous aircraft.
                      </p>
                      <div className="grid grid-cols-4 gap-4 text-sm mb-4" style={{ color: "#60ffd0" }}>
                        <div>{AIRCRAFT_DATABASE["A380"]?.capacity || 525} pax</div>
                        <div>{AIRCRAFT_DATABASE["A380"]?.range || 8000} nm</div>
                        <div>{AIRCRAFT_DATABASE["A380"]?.cruiseSpeed || 560} kts</div>
                        <div>{AIRCRAFT_DATABASE["A380"]?.fuelBurn || 11000} kg/hr</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-3xl font-bold" style={{ color: "#f59e0b" }}>{formatPrice(AIRCRAFT_DATABASE["A380"]?.purchasePrice || 375000000)}</div>
                        <Button
                          onClick={() => {
                            if (!hasEOS || tier < 5) return;
                            // Gold A380: Same plane, but with triggersRareRoll flag for the roll
                            setSelectedAircraft({ ...AIRCRAFT_DATABASE["A380"], model: "A380", triggersRareRoll: true, isGoldA380: true });
                            setPurchaseError(null);
                            setShowPurchaseDialog(true);
                          }}
                          disabled={!hasEOS || tier < 5 || cash < (AIRCRAFT_DATABASE["A380"]?.purchasePrice || 375000000)}
                          style={hasEOS && tier >= 5 && cash >= (AIRCRAFT_DATABASE["A380"]?.purchasePrice || 375000000)
                            ? { backgroundColor: "#f59e0b", color: "#000", fontSize: "1.1rem", padding: "0.75rem 2rem" }
                            : { backgroundColor: "#333", color: "#525252", fontSize: "1.1rem", padding: "0.75rem 2rem", cursor: "not-allowed" }}
                        >
                          {hasEOS && tier >= 5 ? "Purchase + Roll" : "[LOCKED] Purchase + Roll"}
                        </Button>
                      </div>

                      {/* Prerequisite message for non-EOS/non-Tier5 */}
                      {(!hasEOS || tier < 5) && (
                        <div
                          className="mt-4 p-3 rounded-lg text-sm"
                          style={{
                            backgroundColor: "#1a1a1a",
                            border: "1px solid #333",
                            color: "#737373"
                          }}
                        >
                          You haven't fulfilled the prerequisites to purchase this.
                          {!hasEOS && tier < 5 && " (Requires Tier 5 + EOS)"}
                          {!hasEOS && tier >= 5 && " (Requires EOS)"}
                          {hasEOS && tier < 5 && " (Requires Tier 5)"}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Info card about Gold A380 vs Regular A380 */}
              <Card className="overflow-hidden" style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
                <CardContent className="p-4">
                  <h4 className="font-bold mb-2" style={{ color: "#f59e0b" }}>Gold A380 vs Regular A380</h4>
                  <ul className="text-sm space-y-2" style={{ color: "#a3a3a3" }}>
                    <li><span style={{ color: "#737373" }}>Regular A380:</span> Available to anyone with $350M. You get the plane.</li>
                    <li><span style={{ color: "#f59e0b" }}>Gold A380:</span> Same plane + triggers a rare aircraft roll.</li>
                    <li className="pt-2" style={{ color: "#f59e0b" }}>This is the only way to access rare aircraft in the game.</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Roll odds info card */}
              <Card className="overflow-hidden" style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
                <CardContent className="p-4">
                  <h4 className="font-bold mb-3" style={{ color: "#f59e0b" }}>Roll Odds</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span style={{ color: "#22c55e" }}>Rare</span><span style={{ color: "#a3a3a3" }}>79.9%</span></div>
                    <div className="flex justify-between"><span style={{ color: "#b8860b" }}>Relic</span><span style={{ color: "#a3a3a3" }}>10%</span></div>
                    <div className="flex justify-between"><span style={{ color: "#ef4444" }}>Anomalous</span><span style={{ color: "#a3a3a3" }}>5%</span></div>
                    <div className="flex justify-between"><span style={{ color: "#f59e0b" }}>Legendary</span><span style={{ color: "#a3a3a3" }}>5%</span></div>
                    <div className="flex justify-between"><span style={{ color: "#a855f7" }}>Mythic</span><span style={{ color: "#a3a3a3" }}>0.1%</span></div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          {/* YOUR FLEET */}
          {activeTab === "fleet" && ownedAircraft.map((item) => (
            <Card key={item.id} className="overflow-hidden" style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
              <CardContent className="p-4">
                <div className="h-24 rounded mb-3 flex items-center justify-center overflow-hidden" style={{ backgroundColor: "#111" }}>
                  <img
                    src={getAircraftImage(item.model)}
                    alt={item.model}
                    className="w-full h-full object-contain object-center"
                    style={{ filter: "drop-shadow(2px 4px 6px rgba(0,0,0,0.5))" }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  <span className="text-2xl hidden" style={{ color: item.isCollector ? "#f59e0b" : item.isRare ? "#a855f7" : "#333" }}>---</span>
                </div>
                
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold" style={{ color: "#e5e5e5" }}>{item.registration}</h3>
                    <p className="text-xs" style={{ color: "#737373" }}>{item.model}</p>
                  </div>
                  <Badge style={{ 
                    backgroundColor: item.status === "available" ? "#22c55e" : item.status === "flying" ? "#3b82f6" : "#eab308",
                    color: "#000" 
                  }}>
                    {item.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mb-3" style={{ color: "#a3a3a3" }}>
                  <div>{(item.totalFlightHours || 0).toLocaleString()} hrs</div>
                  <div>{item.totalCycles || 0} cycles</div>
                </div>

                {item.isLeased && (
                  <div className="text-xs mb-3 p-2 rounded" style={{ backgroundColor: "#1a1a2a", color: "#93c5fd" }}>
                    Leased | {formatPrice(item.monthlyPayment)}/mo
                  </div>
                )}

                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => handleSell(item)}
                  disabled={item.status === "flying" || item.isLeased}
                  style={{ backgroundColor: "#22c55e", color: "#fff" }}
                >
                  Sell
                </Button>
              </CardContent>
            </Card>
          ))}

          {activeTab === "fleet" && ownedAircraft.length === 0 && (
            <div className="col-span-full text-center py-12" style={{ color: "#525252" }}>
              <p className="text-4xl opacity-30 mb-4">---</p>
              <p>No aircraft in your fleet</p>
              <Button className="mt-4" onClick={() => setActiveTab("new")} style={{ backgroundColor: "#60ffd0", color: "#000" }}>
                Browse Aircraft
              </Button>
            </div>
          )}
        </div>

        {/* PURCHASE DIALOG */}
        <Dialog open={showPurchaseDialog} onOpenChange={(open) => { setShowPurchaseDialog(open); if (!open) setPurchaseQuantity(1); }}>
          <DialogContent style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a", color: "#e5e5e5" }}>
            <DialogHeader>
              <DialogTitle style={{ color: "#60ffd0" }}>Purchase {selectedAircraft?.model || selectedAircraft?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Quantity Selector - only for non-used, non-collector, non-Gold A380 (which triggers a roll) */}
              {!selectedAircraft?.isUsed && !selectedAircraft?.isCollector && !selectedAircraft?.triggersRareRoll && (
                <div className="p-4 rounded" style={{ backgroundColor: "#111" }}>
                  <div className="text-sm mb-2" style={{ color: "#737373" }}>How many would you like to purchase?</div>
                  <div className="flex items-center justify-center gap-1">
                    <button
                      onClick={() => setPurchaseQuantity(q => Math.max(1, q - 1))}
                      disabled={purchaseQuantity <= 1}
                      className="text-2xl font-bold px-3 py-1 rounded transition-colors"
                      style={{
                        color: purchaseQuantity <= 1 ? "#333" : "#60ffd0",
                        backgroundColor: "transparent",
                        cursor: purchaseQuantity <= 1 ? "not-allowed" : "pointer"
                      }}
                    >
                      &lt;
                    </button>
                    <div
                      className="text-2xl font-bold px-4 py-1 rounded min-w-[60px] text-center"
                      style={{ backgroundColor: "#1a1a1a", color: "#60ffd0", border: "1px solid #333" }}
                    >
                      {purchaseQuantity}
                    </div>
                    <button
                      onClick={() => {
                        const unitPrice = selectedAircraft?.price || selectedAircraft?.purchasePrice || 0;
                        const maxAfford = Math.floor(cash / unitPrice);
                        setPurchaseQuantity(q => Math.min(maxAfford, q + 1));
                      }}
                      disabled={cash < (selectedAircraft?.price || selectedAircraft?.purchasePrice || 0) * (purchaseQuantity + 1)}
                      className="text-2xl font-bold px-3 py-1 rounded transition-colors"
                      style={{
                        color: cash < (selectedAircraft?.price || selectedAircraft?.purchasePrice || 0) * (purchaseQuantity + 1) ? "#333" : "#60ffd0",
                        backgroundColor: "transparent",
                        cursor: cash < (selectedAircraft?.price || selectedAircraft?.purchasePrice || 0) * (purchaseQuantity + 1) ? "not-allowed" : "pointer"
                      }}
                    >
                      &gt;
                    </button>
                  </div>
                  {purchaseQuantity > 1 && (
                    <div className="text-xs mt-2 text-center" style={{ color: "#737373" }}>
                      {formatPrice(selectedAircraft?.price || selectedAircraft?.purchasePrice)} each
                    </div>
                  )}
                </div>
              )}

              <div className="p-4 rounded" style={{ backgroundColor: "#111" }}>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total{purchaseQuantity > 1 ? ` (×${purchaseQuantity})` : ''}</span>
                  <span style={{ color: "#22c55e" }}>
                    {formatPrice((selectedAircraft?.price || selectedAircraft?.purchasePrice || 0) * purchaseQuantity)}
                  </span>
                </div>
                <div className="text-xs mt-2" style={{ color: "#22c55e" }}>
                  Immediate delivery included
                </div>
              </div>

              {/* Only Gold A380 (triggersRareRoll flag) triggers the roll, not regular A380 */}
              {selectedAircraft?.triggersRareRoll && hasEOS && (
                <div className="p-3 rounded" style={{ backgroundColor: "#3d2a0a", border: "1px solid #f59e0b" }}>
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#f59e0b" }}>
                    <span>*** GOLD A380 - This purchase triggers a RARE AIRCRAFT ROLL! ***</span>
                  </div>
                </div>
              )}

              {purchaseError && (
                <div className="p-3 rounded" style={{ backgroundColor: "#3d0a0a", border: "1px solid #ef4444" }}>
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#ef4444" }}>
                    <span>! {purchaseError}</span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={() => { setShowPurchaseDialog(false); setPurchaseError(null); setPurchaseQuantity(1); }}
                disabled={isPurchasing}
                style={{ backgroundColor: "#333", color: "#e5e5e5" }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handlePurchase(selectedAircraft, purchaseQuantity)}
                disabled={isPurchasing || cash < (selectedAircraft?.price || selectedAircraft?.purchasePrice || 0) * purchaseQuantity}
                style={
                  !isPurchasing && cash >= (selectedAircraft?.price || selectedAircraft?.purchasePrice || 0) * purchaseQuantity
                    ? { backgroundColor: "#60ffd0", color: "#000" }
                    : { backgroundColor: "#333", color: "#666", cursor: "not-allowed" }
                }
              >
                {isPurchasing ? "Processing..." : purchaseQuantity > 1 ? `Purchase ${purchaseQuantity} Aircraft` : "Confirm Purchase"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* LEASE DIALOG */}
        <Dialog open={showLeaseDialog} onOpenChange={setShowLeaseDialog}>
          <DialogContent style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a", color: "#e5e5e5" }}>
            <DialogHeader>
              <DialogTitle style={{ color: "#60ffd0" }}>Lease {selectedAircraft?.model}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm" style={{ color: "#737373" }}>
                Select lease term. Longer terms have lower monthly payments.
              </div>
              
              <div className="space-y-2">
                {Object.entries(LEASE_TERMS).map(([months, term]) => {
                  const monthly = Math.round((selectedAircraft?.leasePrice || 0) * term.multiplier);
                  const deposit = monthly * term.deposit;
                  return (
                    <label 
                      key={months}
                      className="flex items-center gap-3 p-3 rounded cursor-pointer"
                      style={{ backgroundColor: selectedLeaseTerm === parseInt(months) ? "#1a2a1a" : "#111" }}
                    >
                      <input 
                        type="radio" 
                        name="leaseTerm" 
                        checked={selectedLeaseTerm === parseInt(months)} 
                        onChange={() => setSelectedLeaseTerm(parseInt(months))} 
                      />
                      <div className="flex-1">
                        <div className="font-bold">{term.label}</div>
                        <div className="text-xs" style={{ color: "#737373" }}>
                          {term.deposit > 0 ? `${term.deposit} month deposit required` : "No deposit"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold" style={{ color: "#22c55e" }}>{formatPrice(monthly)}/mo</div>
                        {deposit > 0 && <div className="text-xs" style={{ color: "#737373" }}>Deposit: {formatPrice(deposit)}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="p-3 rounded" style={{ backgroundColor: "#1a1a1a" }}>
                <div className="text-xs" style={{ color: "#737373" }}>
                  Early termination penalty: {LEASE_TERMS[selectedLeaseTerm]?.penalty * 100}% of remaining payments
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowLeaseDialog(false)} style={{ backgroundColor: "#333", color: "#e5e5e5" }}>
                Cancel
              </Button>
              <Button
                onClick={() => handleLease(selectedAircraft)}
                disabled={leaseLoading}
                style={{ backgroundColor: "#22c55e", color: "#000" }}
              >
                {leaseLoading ? "Processing..." : "Sign Lease"}
              </Button>
              
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* BONUS RARE AIRCRAFT REVEAL - Pokemon-style excitement! */}
        <Dialog open={showRareRollDialog} onOpenChange={setShowRareRollDialog}>
          <DialogContent
            className="max-w-lg"
            style={{
              backgroundColor: "#0a0a0a",
              borderWidth: "3px",
              borderColor: getRarityColor(rareRollResult?.rarity).primary,
              color: "#e5e5e5",
              boxShadow: `0 0 60px ${getRarityColor(rareRollResult?.rarity).primary}80, 0 0 120px ${getRarityColor(rareRollResult?.rarity).primary}40`
            }}
          >
            {rareRollResult && (() => {
              const colors = getRarityColor(rareRollResult.rarity);
              return (
                <div className="text-center py-4">
                  {/* Sparkle header */}
                  <div className="text-sm uppercase tracking-widest mb-2" style={{ color: "#525252" }}>
                    Your GOLD A380 purchase triggered a bonus...
                  </div>

                  {/* Big reveal text */}
                  <h1 className="text-4xl font-black mb-4 animate-pulse" style={{
                    color: colors.primary,
                    textShadow: `0 0 20px ${colors.primary}cc`
                  }}>
                    {colors.label}
                  </h1>

                  {/* Aircraft image with glow */}
                  <div className="w-48 h-48 mx-auto mb-4 rounded-lg flex items-center justify-center relative overflow-hidden" style={{
                    background: `radial-gradient(circle, ${colors.primary}4d 0%, ${colors.primary}1a 50%, transparent 70%)`,
                    boxShadow: `0 0 40px ${colors.primary}60`
                  }}>
                    <div className="absolute inset-0 rounded-lg animate-ping opacity-10" style={{
                      backgroundColor: colors.primary
                    }} />
                    <img
                      src={rareRollResult.aircraft?.image || getCollectibleImagePath(rareRollResult.aircraft?.aircraftId || rareRollResult.aircraft?.model || rareRollResult.aircraft?.id, rareRollResult.rarity)}
                      alt={rareRollResult.aircraft?.name || 'Rare Aircraft'}
                      className="w-full h-full object-contain p-4 relative z-10"
                      style={{
                        filter: `drop-shadow(0 0 15px ${colors.primary})`
                      }}
                      onError={(e) => {
                        // Fallback to Plane icon if image fails
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="absolute inset-0 items-center justify-center hidden" style={{ display: 'none' }}>
                      <span className="text-4xl font-bold" style={{
                        color: colors.primary,
                        filter: `drop-shadow(0 0 10px ${colors.primary})`
                      }}>RARE</span>
                    </div>
                  </div>

                  {/* YOU ALSO RECEIVED */}
                  <div className="text-lg mb-1" style={{ color: "#737373" }}>You also received...</div>

                  {/* Aircraft name - BIG */}
                  <h2 className="text-3xl font-bold mb-3" style={{ color: colors.text }}>
                    {rareRollResult.aircraft?.name}
                  </h2>

                  {/* Description */}
                  <p className="mb-4 text-sm italic" style={{ color: "#a3a3a3" }}>
                    "{rareRollResult.aircraft?.description}"
                  </p>

                  {/* Tradeoffs warning for Anomalous */}
                  {rareRollResult.rarity === "anomalous" && rareRollResult.aircraft?.tradeoffs?.length > 0 && (
                    <div className="text-left p-4 rounded-lg mb-4" style={{
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid #ef444440"
                    }}>
                      <div className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: "#ef4444" }}>
                        WARNING - TRADEOFFS
                      </div>
                      <ul className="text-sm space-y-1">
                        {rareRollResult.aircraft.tradeoffs.map((tradeoff, i) => (
                          <li key={i} style={{ color: "#fca5a5" }}>
                            - {tradeoff.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Special abilities */}
                  {rareRollResult.aircraft?.specialAbilities && rareRollResult.aircraft.specialAbilities.length > 0 && (
                    <div className="text-left p-4 rounded-lg mb-4" style={{
                      backgroundColor: "rgba(0,0,0,0.5)",
                      border: `1px solid ${colors.primary}40`
                    }}>
                      <div className="text-xs font-bold mb-2 uppercase tracking-wider" style={{ color: colors.primary }}>
                        {rareRollResult.rarity === "anomalous" ? "Anomalous Properties" : "Special Abilities"}
                      </div>
                      <ul className="text-sm space-y-1">
                        {rareRollResult.aircraft.specialAbilities.map((ability, i) => (
                          <li key={i} style={{ color: "#e5e5e5" }}>
                            - {ability.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Stats bar */}
                  <div className="grid grid-cols-3 gap-2 text-xs mb-4 p-3 rounded" style={{ backgroundColor: "#111" }}>
                    <div>
                      <div style={{ color: "#525252" }}>Capacity</div>
                      <div className="font-bold">{rareRollResult.aircraft?.type === 'cargo' ? `${((rareRollResult.aircraft?.cargoCapacity || 0) / 1000).toFixed(0)}k lbs` : `${rareRollResult.aircraft?.capacity || 0} pax`}</div>
                    </div>
                    <div>
                      <div style={{ color: "#525252" }}>Range</div>
                      <div className="font-bold">{rareRollResult.aircraft?.range?.toLocaleString() || "???"} nm</div>
                    </div>
                    <div>
                      <div style={{ color: "#525252" }}>Speed</div>
                      <div className="font-bold">{rareRollResult.aircraft?.cruiseSpeed || "???"} kts</div>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={() => setShowRareRollDialog(false)}
                    className="w-full text-lg py-6 font-bold"
                    style={{
                      backgroundColor: colors.primary,
                      color: "#000",
                      boxShadow: `0 0 20px ${colors.primary}60`
                    }}
                  >
                    {colors.button}
                  </Button>

                  {/* Tiny flavor text */}
                  <div className="text-xs mt-3" style={{ color: "#333" }}>
                    Added to your fleet - {rareRollResult.rarity === "anomalous" ? "Good luck." : "Ready to fly"}
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Hub Capacity Modal */}
        <Dialog open={showCapacityModal} onOpenChange={setShowCapacityModal}>
          <DialogContent
            style={{
              backgroundColor: "#0c0c0c",
              borderColor: "#f59e0b",
              borderWidth: "2px",
              color: "#e5e5e5",
              maxWidth: "450px"
            }}
          >
            <DialogHeader>
              <DialogTitle style={{ color: "#f59e0b", fontSize: "1.5rem" }}>
                Hub at Capacity
              </DialogTitle>
            </DialogHeader>

            <div className="py-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4" style={{ color: "#f59e0b" }}>⚠️</div>
                <p className="text-lg" style={{ color: "#e5e5e5" }}>
                  {capacityMessage}
                </p>
              </div>

              <div className="p-4 rounded-lg" style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}>
                <div className="text-sm space-y-2" style={{ color: "#a3a3a3" }}>
                  <div className="flex items-center gap-2">
                    <span style={{ color: "#60ffd0" }}>💡</span>
                    <span>Each hub can hold a maximum of 30 aircraft</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span style={{ color: "#60ffd0" }}>🏢</span>
                    <span>Purchase additional hubs to expand your fleet</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span style={{ color: "#60ffd0" }}>📍</span>
                    <span>New hubs give you 30 more aircraft slots each</span>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button
                onClick={() => {
                  setShowCapacityModal(false);
                  navigate("/hub-store");
                }}
                style={{
                  backgroundColor: "#f59e0b",
                  color: "#000",
                  flex: 1
                }}
              >
                Browse Hubs
              </Button>
              <Button
                onClick={() => setShowCapacityModal(false)}
                style={{
                  backgroundColor: "#333",
                  color: "#e5e5e5",
                  flex: 1
                }}
              >
                OK
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
}